// API Client - shared fetch helper and configuration
// Authentication: Uses httpOnly session cookies

export const API_BASE = import.meta.env.VITE_API_URL || '';
export const API_URL = API_BASE ? `${API_BASE}/api` : '/api';

type AuthTokenProviderOptions = {
  forceRefresh?: boolean;
};

type AuthTokenProvider = (options?: AuthTokenProviderOptions) => Promise<string | null>;

let authTokenProvider: AuthTokenProvider | null = null;

export function setAuthTokenProvider(provider: AuthTokenProvider | null) {
  authTokenProvider = provider;
}

type ApiParamPrimitive = string | number | boolean;
export type ApiParamValue =
  | ApiParamPrimitive
  | null
  | undefined
  | ReadonlyArray<ApiParamPrimitive | null | undefined>;
export type ApiParams = Record<string, ApiParamValue>;

// Default timeout for API requests (30 seconds)
const DEFAULT_TIMEOUT = 30000;
const SAFE_BOOTSTRAP_RETRY_ATTEMPTS = 1;
const MAX_RETRY_DELAY_MS = 3000;
const DEFAULT_CALLER_SERVICE = 'dashboard';
const NATIVE_AUTH_HEADER_ALLOWLIST = new Set([
  'x-cockpit-ios-install-id',
  'x-cockpit-ios-device-secret',
  'x-cockpit-ios-app-version',
]);
const RETRYABLE_STATUS_CODES = new Set([429, 502, 503, 504]);
const SAFE_BOOTSTRAP_RETRY_PATHS = new Set([
  '/api/me',
  '/api/properties',
  '/api/settings/properties',
  '/api/settings/competitors',
  '/api/kpis',
  '/api/pickup/future',
  '/api/rates/decisions',
  '/api/alerts/summary',
]);
const SAFE_BOOTSTRAP_RETRY_PREFIXES = [
  '/api/settings/cleaning-fees/',
  '/api/settings/room-types-for-boundaries/',
];
const inFlightGetRequests = new Map<string, Promise<Response>>();

// Track stale data warnings from backend
let lastCacheWarning: { endpoint: string; age: string; timestamp: number } | null = null;

/**
 * Get the last cache warning (if any)
 */
export function getLastCacheWarning() {
  // Clear warnings older than 5 minutes
  if (lastCacheWarning && Date.now() - lastCacheWarning.timestamp > 5 * 60 * 1000) {
    lastCacheWarning = null;
  }
  return lastCacheWarning;
}

function parsePathname(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return '';
  }
}

function isSafeBootstrapRetryPath(pathname: string): boolean {
  if (!pathname) return false;
  return (
    SAFE_BOOTSTRAP_RETRY_PATHS.has(pathname) ||
    SAFE_BOOTSTRAP_RETRY_PREFIXES.some((prefix) => pathname.startsWith(prefix))
  );
}

function parseRetryAfterMs(headerValue: string | null): number | null {
  if (!headerValue) return null;
  const trimmed = headerValue.trim();
  if (!trimmed) return null;

  // Retry-After can be delta-seconds or an HTTP-date.
  const asSeconds = Number(trimmed);
  if (Number.isFinite(asSeconds) && asSeconds >= 0) {
    return Math.round(asSeconds * 1000);
  }

  const asDate = Date.parse(trimmed);
  if (!Number.isNaN(asDate)) {
    return Math.max(0, asDate - Date.now());
  }

  return null;
}

function computeRetryDelayMs(attempt: number, response?: Response): number {
  const retryAfterMs = parseRetryAfterMs(response?.headers.get('Retry-After') ?? null);
  const jitterMs = Math.floor(Math.random() * 175);

  if (typeof retryAfterMs === 'number') {
    return Math.min(MAX_RETRY_DELAY_MS, Math.max(150, retryAfterMs + jitterMs));
  }

  const exponential = 250 * 2 ** attempt;
  return Math.min(MAX_RETRY_DELAY_MS, exponential + jitterMs);
}

function waitFor(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function shouldRetryStatus(status: number): boolean {
  return RETRYABLE_STATUS_CODES.has(status);
}

function isRetryableError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  if (error.message.startsWith('Request timeout:')) return true;
  // Browser fetch network failures generally surface as TypeError.
  if (error instanceof TypeError) return true;
  return false;
}

/**
 * Get headers for authenticated requests
 * Note: Authentication is handled via httpOnly cookies.
 */
async function getNativeAuthHeaders(): Promise<Record<string, string>> {
  if (typeof window === 'undefined') return {};
  const bridge = (
    window as Window & {
      CockpitIOSAuth?: {
        getAuthHeaders?: () => Promise<Record<string, string>> | Record<string, string>;
      };
    }
  ).CockpitIOSAuth;
  if (!bridge || typeof bridge.getAuthHeaders !== 'function') return {};

  try {
    const rawHeaders = await bridge.getAuthHeaders();
    const headers: Record<string, string> = {};
    for (const [key, value] of Object.entries(rawHeaders || {})) {
      const lower = key.toLowerCase();
      if (!NATIVE_AUTH_HEADER_ALLOWLIST.has(lower)) continue;
      if (typeof value === 'string' && value.trim()) headers[key] = value;
    }
    return headers;
  } catch (error) {
    console.warn('Failed to resolve native auth headers:', error);
    return {};
  }
}

export async function getAuthHeaders(
  additionalHeaders?: Record<string, string>,
  options?: AuthTokenProviderOptions,
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...additionalHeaders,
  };
  const nativeHeaders = await getNativeAuthHeaders();
  Object.assign(headers, nativeHeaders);
  const hasAuthHeader = Object.keys(headers).some((key) => key.toLowerCase() === 'authorization');
  const hasNativeAuth = Object.keys(nativeHeaders).some((key) =>
    NATIVE_AUTH_HEADER_ALLOWLIST.has(key.toLowerCase()),
  );
  if (!hasAuthHeader && !hasNativeAuth && authTokenProvider) {
    try {
      const token = await authTokenProvider(options);
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.warn('Failed to resolve auth token:', error);
    }
  }
  if (!Object.keys(headers).some((key) => key.toLowerCase() === 'x-caller-service')) {
    headers['X-Caller-Service'] = DEFAULT_CALLER_SERVICE;
  }
  return headers;
}

function toParamStrings(value: ApiParamValue): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) return value.flatMap((v) => (v == null ? [] : [String(v)]));
  return [String(value)];
}

/**
 * Build URLSearchParams with stable key ordering (sorted).
 * This reduces accidental cache fragmentation when URLs are used as cache keys.
 */
export function stableSearchParams(params?: ApiParams): URLSearchParams {
  const sp = new URLSearchParams();
  if (!params) return sp;

  const keys = Object.keys(params).sort();
  for (const key of keys) {
    for (const v of toParamStrings(params[key])) sp.append(key, v);
  }

  return sp;
}

/**
 * Build an API endpoint path (no base) with a stable-ordered querystring.
 * Example: buildApiEndpoint('/kpis', { property_name: 'soundry' })
 * -> '/kpis?property_name=soundry'
 */
export function buildApiEndpoint(path: string, params?: ApiParams): string {
  const qs = stableSearchParams(params).toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * Build a full API URL (includes API_URL prefix).
 * Example: buildApiUrl('/kpis', { property_name: 'soundry' })
 * -> '.../api/kpis?property_name=soundry'
 */
export function buildApiUrl(path: string, params?: ApiParams): string {
  return `${API_URL}${buildApiEndpoint(path, params)}`;
}

/**
 * Authenticated fetch wrapper - use this instead of raw fetch()
 * Features:
 * - Session-cookie authentication
 * - Request timeout support (default 30s)
 * - 401 detection (without hard redirect loops)
 * - Tracks X-Cache-Warn headers from backend for stale data detection
 */
export async function authFetch(
  url: string,
  options?: RequestInit & { timeout?: number },
): Promise<Response> {
  const { timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options || {};
  const t0 = import.meta.env.DEV ? performance.now() : 0;
  const method = (fetchOptions.method || 'GET').toUpperCase();
  const pathname = parsePathname(url);
  const safeRetryEligible = method === 'GET' && isSafeBootstrapRetryPath(pathname);
  const externalSignal = fetchOptions.signal;
  const dedupeKey =
    method === 'GET' && !fetchOptions.body && !externalSignal ? `${method}:${url}` : null;

  const executeRequest = async (): Promise<Response> => {
    let attempt = 0;
    let forceRefreshAuth = false;

    while (true) {
      const controller = new AbortController();

      if (externalSignal) {
        if (externalSignal.aborted) {
          controller.abort();
        } else {
          externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
        }
      }

      const timeoutId = timeout > 0 ? setTimeout(() => controller.abort(), timeout) : null;

      try {
        const headers = await getAuthHeaders(fetchOptions?.headers as Record<string, string>, {
          forceRefresh: forceRefreshAuth,
        });
        if (typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData) {
          for (const key of Object.keys(headers)) {
            if (key.toLowerCase() === 'content-type') delete headers[key];
          }
        }
        // Don't hard-redirect on 401 here. In Clerk mode, transient/partial 401s can
        // create redirect loops (`/login?session=expired`). Route guards + auth context
        // decide navigation; authFetch only surfaces the failure.
        const response = await fetch(url, {
          ...fetchOptions,
          headers,
          credentials: 'include',
          signal: controller.signal,
        });

        // Session expired or unauthenticated.
        if (response.status === 401) {
          if (attempt < SAFE_BOOTSTRAP_RETRY_ATTEMPTS) {
            const delayMs = computeRetryDelayMs(attempt, response);
            attempt += 1;
            forceRefreshAuth = true;
            await waitFor(delayMs);
            continue;
          }
          throw new Error('Authentication failed');
        }

        if (
          safeRetryEligible &&
          shouldRetryStatus(response.status) &&
          attempt < SAFE_BOOTSTRAP_RETRY_ATTEMPTS
        ) {
          const delayMs = computeRetryDelayMs(attempt, response);
          attempt += 1;
          await waitFor(delayMs);
          continue;
        }

        // Check for cache staleness warning from backend
        const cacheWarn = response.headers.get('X-Cache-Warn');
        const cacheAge = response.headers.get('X-Cache-Age');

        if (cacheWarn === 'stale' && cacheAge) {
          lastCacheWarning = {
            endpoint: url,
            age: cacheAge,
            timestamp: Date.now(),
          };
          console.warn(`[Cache] Stale data for ${url}: ${cacheAge}`);
        }

        // DEV-only: single-line trace for cache + latency debugging (pulled from response headers).
        if (import.meta.env.DEV) {
          let path = url;
          try {
            const u = new URL(url, window.location.origin);
            path = `${u.pathname}${u.search}`;
          } catch {
            // ignore
          }

          const serverMsRaw = response.headers.get('X-Request-Duration-MS') || '';
          const durationMs =
            serverMsRaw && /^\d+$/.test(serverMsRaw)
              ? Number(serverMsRaw)
              : Math.round(performance.now() - t0);

          const cacheHit = response.headers.get('X-Cache') || 'BYPASS';
          const cacheLayer = response.headers.get('X-Cache-Layer') || 'none';
          const asOf = response.headers.get('X-Data-As-Of') || '-';
          const querySource = response.headers.get('X-Query-Source') || '-';

          console.info(
            `[api-trace] ${method} ${path} duration_ms=${durationMs} cache_hit=${cacheHit} cache_layer=${cacheLayer} as_of=${asOf} query_source=${querySource}`,
          );

          // DEV-only: optional request leaderboard (installed via src/dev/requestLeaderboard.ts).
          // Avoid importing DEV tooling here to keep prod bundles clean.
          const record = (
            window as Window & {
              __requestLeaderboardRecord?: (rec: {
                t: number;
                method: string;
                path: string;
                status: number;
                duration_ms: number;
                cache_hit: string;
                cache_layer: string;
                as_of: string;
                query_source: string;
              }) => void;
            }
          ).__requestLeaderboardRecord;
          if (typeof record === 'function') {
            record({
              t: Date.now(),
              method,
              path,
              status: response.status,
              duration_ms: durationMs,
              cache_hit: cacheHit,
              cache_layer: cacheLayer,
              as_of: asOf,
              query_source: querySource,
            });
          }

          // DEV-only: initial load trace hook (installed via src/dev/initialLoadTrace.ts).
          const recordInitialLoad = (
            window as Window & {
              __initialLoadTraceRecordRequest?: (rec: {
                t: number;
                method: string;
                path: string;
                status: number;
                duration_ms: number;
                cache_hit: string;
                cache_layer: string;
                as_of: string;
                query_source: string;
              }) => void;
            }
          ).__initialLoadTraceRecordRequest;
          if (typeof recordInitialLoad === 'function') {
            recordInitialLoad({
              t: Date.now(),
              method,
              path,
              status: response.status,
              duration_ms: durationMs,
              cache_hit: cacheHit,
              cache_layer: cacheLayer,
              as_of: asOf,
              query_source: querySource,
            });
          }
        }

        return response;
      } catch (error) {
        const wrappedError =
          error instanceof Error && error.name === 'AbortError'
            ? new Error(`Request timeout: ${url} did not respond within ${timeout}ms`)
            : error;

        if (
          safeRetryEligible &&
          attempt < SAFE_BOOTSTRAP_RETRY_ATTEMPTS &&
          isRetryableError(wrappedError)
        ) {
          const delayMs = computeRetryDelayMs(attempt);
          attempt += 1;
          await waitFor(delayMs);
          continue;
        }

        throw wrappedError;
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    }
  };

  if (!dedupeKey) {
    return executeRequest();
  }

  const inFlight = inFlightGetRequests.get(dedupeKey);
  if (inFlight) {
    const sharedResponse = await inFlight;
    return sharedResponse.clone();
  }

  const sharedRequest = executeRequest();
  inFlightGetRequests.set(dedupeKey, sharedRequest);
  try {
    const response = await sharedRequest;
    return response.clone();
  } finally {
    inFlightGetRequests.delete(dedupeKey);
  }
}

/**
 * Custom API error with HTTP status for retry logic
 */
export class APIError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'APIError';
    this.status = status;
  }
}

/**
 * Typed API fetch with JSON parsing
 */
export async function fetchAPI<T>(
  endpoint: string,
  options?: RequestInit & { timeout?: number },
): Promise<T> {
  // Defensive: prevent accidental double-prefixing (/api/api/...) which can fragment caches.
  if (import.meta.env.DEV && endpoint.startsWith('/api/')) {
    console.warn(`[api] fetchAPI() endpoint starts with '/api/': ${endpoint}`);
  }

  const response = await authFetch(`${API_URL}${endpoint}`, options);

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    // Throw APIError with status for smart retry logic in QueryClient
    throw new APIError(
      `API Error: ${response.status} ${response.statusText}${errorBody ? ` - ${errorBody}` : ''}`,
      response.status,
    );
  }

  return response.json();
}

/**
 * Append bypass_cache param ONLY in DEV mode.
 * Production always uses cache.
 */
export function appendBypassParam(url: string): string {
  if (import.meta.env.DEV) {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}bypass_cache=1`;
  }
  return url;
}
